import React, { MutableRefObject } from "react";
import { useThree } from "@react-three/fiber";
import { useGesture } from "@use-gesture/react";

import { Group, Plane, Vector2, Vector3 } from "three";
import { useStoreSystem } from "../../../store";
import { Axis } from "./types";
import { SideTypeWithoutTop } from "../../../store/types/store";

interface UniversalDraggableProps {
    decorRef?: MutableRefObject<Group | null>;
    position: number[];
    children: React.ReactNode;
    onDragStart(): void;
    onDrag(position: [number, number, number]): void;
    onDragEnd(): void;
    onClick(): void;
    onEdit(): void;

    side?: SideTypeWithoutTop;
    isWallDecor?: boolean;
    availableAxis?: Axis[];
}

export const UniversalDraggable: React.FC<UniversalDraggableProps> = (props) => {
    const {
        children,
        position,
        onDragStart,
        onDragEnd,
        onDrag,
        onClick,
        onEdit,
        availableAxis = ["x", "z"],
        side,
        isWallDecor = false,
        decorRef,
    } = props;

    const location = useStoreSystem((state) => state.location);
    const { raycaster, size, camera, gl } = useThree();
    const currentSide = useStoreSystem((state) => state.currentSide);

    const { mouse2D, plane } = {
        mouse2D: new Vector2(),
        plane: new Plane(),
    };

    let movedDistace: any[] = [];

    // Функция для проверки, можно ли выполнять действие
    const canPerformAction = () => {
        if (location?.includes("view")) {
            return false;
        }
        if (isWallDecor && side !== currentSide) {
            return false;
        }
        return true;
    };

    const bind = useGesture({
        onDrag: (state: Record<string, any>) => {
            if (!canPerformAction()) {
                return;
            }

            const {
                xy: [x, y],
                delta,
                distance,
                event,
            } = state;
            event.stopPropagation();

            movedDistace = distance;
            if (delta[0] === 0 && delta[1] === 0) return;

            // Получаем позицию канваса относительно окна
            const canvasElement = gl.domElement;
            const rect = canvasElement?.getBoundingClientRect() || { left: 0, top: 0 };
            
            // Корректируем координаты мыши с учетом позиции канваса
            const canvasX = x - rect.left;
            const canvasY = y - rect.top;
            
            const nx = (canvasX / size.width) * 2 - 1;
            const ny = (-canvasY / size.height) * 2 + 1;
            mouse2D.set(nx, ny);
            raycaster.setFromCamera(mouse2D, camera);
            const mouse3D = new Vector3(position[0], position[1], position[2]);

            const normal = camera.position.clone().sub(mouse3D).normalize();
            plane.setFromNormalAndCoplanarPoint(normal, mouse3D);

            const newPos = new Vector3();
            raycaster.ray.intersectPlane(plane, newPos);

            onDrag([
                availableAxis.includes("x") ? newPos.x : position[0],
                availableAxis.includes("y") ? newPos.y : position[1],
                availableAxis.includes("z") ? newPos.z : position[2],
            ]);
        },

        onDragStart: ({ event }) => {
            if (!canPerformAction()) {
                return;
            }
            event.stopPropagation();
            onDragStart();
            onClick();
        },

        onDragEnd: ({ event }) => {
            if (!canPerformAction()) {
                return;
            }
            if (movedDistace[0] === 0 && movedDistace[1] === 0) {
                onEdit();
                return;
            }
            onDragEnd();
        },
    });

    return (
        // @ts-ignore
        <group ref={decorRef} {...bind()}>
            {children}
        </group>
    );
};
